import React from 'react';
import { motion } from 'framer-motion';
interface CircleVisualProps {
  radius?: number;
  diameter?: number;
  circumference?: number;
  area?: number;
  label?: string;
  showFormula?: boolean;
  highlightRadius?: boolean;
  highlightDiameter?: boolean;
  sectorAngle?: number;
  highlightSector?: boolean;
  mode?: 'stimulus' | 'support';
}
/**
 * Helper: build an SVG arc path for a sector from 0° to `angleDeg`.
 * The sector starts at 12-o'clock (−90°) and sweeps clockwise.
 */
function sectorPath(cx: number, cy: number, r: number, angleDeg: number): string {
  if (angleDeg >= 360) {
    // Full circle — use two arcs
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`;
  }
  const startRad = -Math.PI / 2; // 12 o'clock
  const endRad = startRad + angleDeg * Math.PI / 180;
  const largeArc = angleDeg > 180 ? 1 : 0;
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}
export function CircleVisual({
  radius: propRadius,
  diameter: propDiameter,
  circumference,
  area,
  label,
  showFormula = false,
  highlightRadius = false,
  highlightDiameter = false,
  sectorAngle,
  highlightSector = false,
  mode = 'support'
}: CircleVisualProps) {
  // Calculate missing values
  const radius = propRadius ?? (propDiameter ? propDiameter / 2 : 50);
  const diameter = propDiameter ?? radius * 2;
  // SVG dimensions
  const svgSize = 280;
  const cx = svgSize / 2;
  const cy = svgSize / 2;
  // Scale circle to fill SVG nicely — ensure minimum visible size
  const maxDisplayRadius = svgSize * 0.38; // ~106px
  const minDisplayRadius = 40;
  const circleRadius = Math.max(maxDisplayRadius, minDisplayRadius);
  const hasSector = typeof sectorAngle === 'number' && sectorAngle > 0 && sectorAngle < 360;
  return <div className="flex flex-col items-center gap-4 py-3" data-id="element-7882">
      {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-7883">
          {label}
        </p>}

      {/* Circle SVG */}
      <motion.svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`} initial={{
      opacity: 0,
      scale: 0.9
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4
    }} className="drop-shadow-md" role="img" aria-label={hasSector ? `Cirkel met sector van ${sectorAngle}°` : 'Cirkel'} data-id="element-7884">
        {/* Full circle background */}
        <motion.circle cx={cx} cy={cy} r={circleRadius} fill={hasSector ? 'rgb(241, 245, 249)' : 'rgb(191, 219, 254)'} stroke="rgb(59, 130, 246)" strokeWidth="3" initial={{
        pathLength: 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 1,
        ease: 'easeInOut'
      }} data-id="element-7885" />

        {/* Sector wedge (when sectorAngle is provided) */}
        {hasSector && <motion.path d={sectorPath(cx, cy, circleRadius, sectorAngle)} fill="rgb(147, 197, 253)" stroke="rgb(59, 130, 246)" strokeWidth="2.5" strokeLinejoin="round" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.5,
        ease: 'easeOut'
      }} style={{
        transformOrigin: `${cx}px ${cy}px`
      }} data-id="element-7886" />}

        {/* Sector angle arc indicator */}
        {hasSector && sectorAngle <= 180 && (() => {
        const arcR = circleRadius * 0.25;
        const startRad = -Math.PI / 2;
        const endRad = startRad + sectorAngle * Math.PI / 180;
        const largeArc = sectorAngle > 180 ? 1 : 0;
        const ax1 = cx + arcR * Math.cos(startRad);
        const ay1 = cy + arcR * Math.sin(startRad);
        const ax2 = cx + arcR * Math.cos(endRad);
        const ay2 = cy + arcR * Math.sin(endRad);
        return <path d={`M ${ax1} ${ay1} A ${arcR} ${arcR} 0 ${largeArc} 1 ${ax2} ${ay2}`} fill="none" stroke="rgb(99, 102, 241)" strokeWidth="2" strokeDasharray="4,3" data-id="element-7887" />;
      })()}

        {/* Sector angle label */}
        {hasSector && (() => {
        const labelR = circleRadius * 0.35;
        const midAngleRad = -Math.PI / 2 + sectorAngle * Math.PI / 360;
        const lx = cx + labelR * Math.cos(midAngleRad);
        const ly = cy + labelR * Math.sin(midAngleRad);
        return <text x={lx} y={ly} textAnchor="middle" dominantBaseline="central" className="text-sm font-bold fill-indigo-700" data-id="element-7888">
                {sectorAngle}°
              </text>;
      })()}

        {/* Center point */}
        <circle cx={cx} cy={cy} r="4" fill="rgb(59, 130, 246)" data-id="element-7889" />

        {/* Radius line (if highlighted) */}
        {highlightRadius && <>
            <motion.line x1={cx} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(239, 68, 68)" strokeWidth="3" strokeDasharray="5,5" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-7890" />
            <text x={cx + circleRadius / 2} y={cy - 10} textAnchor="middle" className="text-sm font-bold fill-red-600" data-id="element-7891">
              r = {radius}
            </text>
          </>}

        {/* Diameter line (if highlighted) */}
        {highlightDiameter && <>
            <motion.line x1={cx - circleRadius} y1={cy} x2={cx + circleRadius} y2={cy} stroke="rgb(34, 197, 94)" strokeWidth="3" strokeDasharray="5,5" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          delay: 0.5,
          duration: 0.6
        }} data-id="element-7892" />
            <text x={cx} y={cy + 25} textAnchor="middle" className="text-sm font-bold fill-green-600" data-id="element-7893">
              d = {diameter}
            </text>
          </>}
      </motion.svg>

      {/* Formula display */}
      {mode === 'support' && showFormula && <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-2 max-w-md" data-id="element-7894">
          {circumference !== undefined && <div className="flex items-center justify-between gap-4" data-id="element-7895">
              <span className="text-sm font-semibold text-blue-900" data-id="element-7896">
                Omtrek (C):
              </span>
              <span className="text-base font-bold text-blue-700" data-id="element-7897">
                C = 2πr = 2 × π × {radius} ≈ {circumference.toFixed(1)} cm
              </span>
            </div>}
          {area !== undefined && <div className="flex items-center justify-between gap-4" data-id="element-7898">
              <span className="text-sm font-semibold text-blue-900" data-id="element-7899">
                Oppervlakte (A):
              </span>
              <span className="text-base font-bold text-blue-700" data-id="element-7900">
                A = πr² = π × {radius}² ≈ {area.toFixed(1)} cm²
              </span>
            </div>}
          {!circumference && !area && <div className="space-y-1" data-id="element-7901">
              <p className="text-sm text-blue-900" data-id="element-7902">
                <span className="font-bold" data-id="element-7903">Omtrek:</span> C = 2πr of C = πd
              </p>
              <p className="text-sm text-blue-900" data-id="element-7904">
                <span className="font-bold" data-id="element-7905">Oppervlakte:</span> A = πr²
              </p>
            </div>}
        </div>}

      {/* Sector info card (when sector is shown) */}
      {hasSector && mode === 'support' && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-7906">
          <div className="flex flex-col gap-1" data-id="element-7907">
            <div className="flex items-center gap-2" data-id="element-7908">
              <span className="text-sm font-semibold text-indigo-900" data-id="element-7909">
                Sector:
              </span>
              <span className="text-lg font-black text-indigo-700" data-id="element-7910">
                {sectorAngle}°
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-7911">
              <span className="text-sm font-semibold text-indigo-900" data-id="element-7912">
                Deel:
              </span>
              <span className="text-lg font-black text-indigo-700" data-id="element-7913">
                {sectorAngle}/360
              </span>
            </div>
          </div>
        </div>}

      {/* Measurements card */}
      {mode === 'support' && (propRadius || propDiameter) && !hasSector && <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-7914">
          <div className="flex flex-col gap-1" data-id="element-7915">
            {propRadius && <div className="flex items-center gap-2" data-id="element-7916">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-7917">
                  Straal (r):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-7918">
                  {radius} cm
                </span>
              </div>}
            {propDiameter && <div className="flex items-center gap-2" data-id="element-7919">
                <span className="text-sm font-semibold text-indigo-900" data-id="element-7920">
                  Diameter (d):
                </span>
                <span className="text-lg font-black text-indigo-700" data-id="element-7921">
                  {diameter} cm
                </span>
              </div>}
          </div>
        </div>}
    </div>;
}